import InstagramStoriesQuery from "@apps/instagram/queries/InstagramStoriesQuery";
import InstagramStoriesStore from "@apps/instagram/stores/InstagramStoriesStore";
import {
    BehaviorSubject,
    Subscription,
    combineLatest,
    interval,
    of,
} from "rxjs";
import {
    filter,
    map,
    scan,
    startWith,
    switchMap,
    takeWhile,
    tap,
} from "rxjs/operators";

import { VideoController } from "@components/VideoPlayer/VideoController";

const IMAGE_TIME = 7000;
const TIMER_STEP = 20;
const IMAGE_STORY = "image";
const VIDEO_STORY = "video";
class InstagramStoryModalManager {
    active$ = this.storiesQuery.active$;
    pause$ = new BehaviorSubject<boolean>(false);
    progress$ = new BehaviorSubject(0);
    destroySub: Subscription[] = [];
    mediaLoaded$ = new BehaviorSubject<boolean>(false);
    videoController$ = new BehaviorSubject<VideoController | null>(null);
    progressSub: Subscription | null = null;
    constructor(
        private readonly storiesQuery: InstagramStoriesQuery,
        private readonly storiesStore: InstagramStoriesStore
    ) {
        const videoPausePlay = combineLatest([
            this.videoController$.pipe(
                filter((vc): vc is VideoController => !!vc)
            ),
            this.pause$,
        ]).pipe(
            map(([vc, pause]) => {
                if (pause) {
                    vc.pause();
                } else {
                    vc.play();
                }
            })
        );

        this.destroySub.push(
            this.active$
                .pipe(
                    filter((v) => !!v),
                    map((story) => story?.mediaType)
                )
                .subscribe((type) => {
                    this.mediaLoaded$.next(false);
                    if (type !== VIDEO_STORY) {
                        this.videoController$.next(null);
                    }
                }),
            videoPausePlay.subscribe()
        );
    }
    changeActiveStory(id: number): void {
        this.resetProggres();
        if (!this.storiesQuery.hasEntity(id)) {
            throw new Error(`Story with id ${id} does not exists in store`);
        }
        this.storiesStore.setActive(id);
        this.startTimer();
    }
    startTimer() {
        const progress$ = this.active$.pipe(
            switchMap((story) => {
                if (story?.mediaType === IMAGE_STORY) {
                    const duration = IMAGE_TIME / TIMER_STEP;
                    return combineLatest([this.pause$, this.mediaLoaded$]).pipe(
                        switchMap(([pause, mediaLoaded]) =>
                            !pause && mediaLoaded ? interval(TIMER_STEP) : of()
                        ),
                        scan((time) => time + 1, 0),
                        takeWhile((time) => time <= duration, false),
                        map((time) => (time / duration) * 100)
                    );
                } else {
                    return this.videoController$.pipe(
                        filter((vc): vc is VideoController => !!vc),
                        switchMap((vc) => vc.progress$)
                    );
                }
            }),
            tap((progress) => {
                if (Math.round(progress) >= 100) this.next();
            }),
            startWith(0)
        );
        this.progressSub = progress$.subscribe(this.progress$);
    }
    resetProggres(): void {
        this.videoController$.getValue()?.seek(0);
        this.progress$.next(0);
        this.progressSub?.unsubscribe();
    }
    /**
     * Go to the next post in list
     */
    next(): void {
        this.resetProggres();
        this.videoController$.next(null);
        this.storiesStore.setActive({
            next: true,
        });
        this.startTimer();
    }
    /**
     * Go to the previous post in list
     */
    prev(): void {
        this.resetProggres();
        this.videoController$.next(null);
        this.storiesStore.setActive({
            prev: true,
        });
        this.startTimer();
    }

    pauseStory(): void {
        this.pause$.next(true);
    }

    playStory(): void {
        this.pause$.next(false);
    }

    selectMediaLoaded(): void {
        this.mediaLoaded$.next(true);
    }

    selectVideoController(vc: VideoController): void {
        this.videoController$.next(vc);
    }

    destroy() {
        this.destroySub.forEach((sub) => {
            sub.unsubscribe();
        });
        this.resetProggres();
    }
}

export default InstagramStoryModalManager;
